import { File, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { locationQuery, type FolderLocation } from './explorerApi';
import './file-thumbnail.css';

const media = /\.(jpg|jpeg|png|gif|webp|bmp|tif|tiff|dpx|exr|mov|mp4|mkv|avi|webm|m4v|flv)$/i;
const video = /\.(mov|mp4|mkv|avi|webm|m4v|flv)$/i;
let active = 0;
const waiting: Array<() => void> = [];
function run<T>(task: () => Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    const start = () => {
      if (signal.aborted) { reject(new DOMException('Aborted', 'AbortError')); return; }
      if (active >= 2) { waiting.push(start); return; }
      active++;
      void task().then(resolve, reject).finally(() => { active--; waiting.shift()?.(); });
    };
    const cancel = () => { const i = waiting.indexOf(start); if (i >= 0) waiting.splice(i, 1); reject(new DOMException('Aborted', 'AbortError')); };
    signal.addEventListener('abort', cancel, { once: true });
    start();
  });
}
const delay = (signal: AbortSignal) => new Promise<void>((resolve, reject) => {
  const abort = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
  const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, 2000);
  signal.addEventListener('abort', abort, { once: true });
});

export function FileThumbnail({ location, name, version, url }: { location: FolderLocation; name: string; version: string; url?: string }) {
  const target = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [image, setImage] = useState<{ key: string; url: string } | null>(null);
  const cached = useRef<{ key: string; url: string } | null>(null);
  const query = locationQuery(location).toString();
  const key = (url || query) + ':' + version;
  let thumbnailUrl = url || `/api/explorer/thumbnail?${query}&v=${encodeURIComponent(version)}`;
  const exr = /\.exr$/i.test(name);
  if (exr) {
    // Reuse the protected EXR renderer and its cache, requesting only a tile-sized image.
    const preview = new URL(thumbnailUrl, window.location.origin);
    preview.pathname = preview.pathname.replace(/\/thumbnail$/, '/preview');
    preview.searchParams.set('exr', 'thumbnail');
    preview.searchParams.set('w', '320');
    preview.searchParams.set('h', '180');
    thumbnailUrl = preview.toString();
  }
  useEffect(() => () => { if (cached.current) URL.revokeObjectURL(cached.current.url); cached.current = null; }, [key]);
  useEffect(() => {
    if (!media.test(name) || !target.current) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { rootMargin: '80px' });
    observer.observe(target.current);
    return () => observer.disconnect();
  }, [name]);
  useEffect(() => {
    if (!visible || !media.test(name) || cached.current?.key === key) return;
    const abort = new AbortController(); const { signal } = abort;
    void run(async () => {
      for (let i = 0; i < 12; i++) {
        const response = await fetch(thumbnailUrl, { credentials: 'same-origin', cache: 'no-store', signal });
        if (response.status === 202 || (exr && response.status === 429)) { await delay(signal); continue; }
        if (!response.ok || !response.headers.get('content-type')?.startsWith('image/')) return;
        const blob = await response.blob();
        if (signal.aborted) return;
        const next = { key, url: URL.createObjectURL(blob) }; cached.current = next; setImage(next); return;
      }
    }, signal).catch(() => { /* Keep the file icon if this file cannot be decoded. */ });
    return () => abort.abort();
  }, [visible, name, key, thumbnailUrl, exr]);
  return <span className="sx-thumbnail" ref={target}>{image?.key === key ? <img src={image.url} alt="" decoding="async"/> : <File size={49}/>} {video.test(name) ? <span className="sx-thumbnail-play"><Play size={13} fill="currentColor"/></span> : null}</span>;
}
